import { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Checkbox, Chip, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { useTranslation } from 'react-i18next'
import { respondentBaseService, type AddressBookRespondent, type RespondentTag } from '@/services/respondentBaseService'
import { DeskInput, Field, Group, StepHeading, SwitchRow } from './desk'
import { LaunchChecklist } from './LaunchChecklist'
import type { DraftPatch, StudyDraft } from './types'

type StepProps = {
  draft: StudyDraft
  onChange: (patch: DraftPatch) => void
}

type Recipient = { kind: 'tag'; tag: RespondentTag } | { kind: 'person'; person: AddressBookRespondent }

const recipientKey = (r: Recipient) => (r.kind === 'tag' ? `t${r.tag.id}` : `p${r.person.id}`)

export default function WizardStepDistribution({ draft, onChange }: StepProps) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')

  return (
    <>
      <StepHeading title={t('wizard.desk.distribution.title')} lead={t('wizard.desk.distribution.lead')} />

      <Group title={t('wizard.desk.distribution.channelsTitle')} description={t('wizard.desk.distribution.channelsDesc')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#F0F0F4', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <LinkOutlinedIcon sx={{ fontSize: 19, color: c.ink72 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{t('wizard.desk.distribution.linkTitle')}</Typography>
            <Typography sx={{ fontSize: 13, color: c.ink56, lineHeight: 1.45 }}>{t('wizard.desk.distribution.linkDesc')}</Typography>
          </Box>
        </Box>
        <InvitesField draft={draft} onChange={onChange} />
      </Group>

      <Group title={t('wizard.desk.distribution.limitsTitle')} description={t('wizard.desk.distribution.limitsDesc')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Field label={t('wizard.distribution.limitLabel')} optional={t('wizard.desk.optional')}>
            <DeskInput
              type="number"
              value={draft.responseLimit}
              onChange={(e) => onChange({ responseLimit: e.target.value })}
              suffix={t('wizard.distribution.limitSuffix')}
              placeholder={t('wizard.desk.distribution.noLimit')}
              slotProps={{ htmlInput: { min: 1, 'aria-label': t('wizard.distribution.limitLabel') } }}
            />
          </Field>
          <Field label={t('wizard.distribution.expiryLabel')} optional={t('wizard.desk.optional')}>
            <DeskInput
              type="date"
              value={draft.expiry}
              onChange={(e) => onChange({ expiry: e.target.value })}
              slotProps={{ htmlInput: { 'aria-label': t('wizard.distribution.expiryLabel') } }}
            />
          </Field>
        </Box>
      </Group>

      <Group title={t('wizard.desk.distribution.accessTitle')} description={t('wizard.desk.distribution.accessDesc')} flush>
        <SwitchRow
          checked={draft.requireAccessCode}
          disabled={draft.isPublic}
          onChange={(requireAccessCode) => onChange({ requireAccessCode })}
          title={t('wizard.distribution.accessCodeTitle')}
          sub={draft.isPublic ? t('wizard.desk.distribution.codeVsPublic') : t('wizard.distribution.accessCodeSub')}
        >
          {draft.requireAccessCode && !draft.isPublic && (
            <Box sx={{ maxWidth: 280 }}>
              <Field label={t('wizard.distribution.accessCodeFieldLabel')} help={t('wizard.distribution.accessCodeFieldHint')}>
                <DeskInput
                  value={draft.accessCode}
                  onChange={(e) => onChange({ accessCode: e.target.value.replace(/\s/g, '').slice(0, 12) })}
                  slotProps={{ htmlInput: { maxLength: 12, 'aria-label': t('wizard.distribution.accessCodeFieldLabel') } }}
                  sx={{ '& input': { letterSpacing: '.12em', fontWeight: 700 } }}
                />
              </Field>
            </Box>
          )}
        </SwitchRow>
        <SwitchRow
          checked={draft.isPublic}
          // A public study is open to every logged-in respondent, so an access
          // code makes no sense — making it public clears the code requirement.
          onChange={(isPublic) => onChange(isPublic ? { isPublic, requireAccessCode: false } : { isPublic })}
          title={t('wizard.desk.distribution.publicTitle')}
          sub={t('wizard.distribution.publicSub')}
        />
      </Group>

      <LaunchChecklist draft={draft} />
    </>
  )
}

/**
 * E-mail invitations as one field: groups and people from the address book,
 * picked by typing. Each recipient gets a personal, tracked link at launch;
 * the public link keeps working for everybody else.
 */
function InvitesField({ draft, onChange }: StepProps) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const [people, setPeople] = useState<AddressBookRespondent[] | null>(null)
  const [tags, setTags] = useState<RespondentTag[]>([])

  useEffect(() => {
    let cancelled = false
    Promise.all([respondentBaseService.listRespondents(), respondentBaseService.listTags()])
      .then(([p, tg]) => {
        if (cancelled) return
        setPeople(p)
        setTags(tg)
      })
      .catch(() => {
        if (cancelled) return
        setPeople([])
        setTags([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const options: Recipient[] = useMemo(
    () => [...tags.map((tag) => ({ kind: 'tag' as const, tag })), ...(people ?? []).map((person) => ({ kind: 'person' as const, person }))],
    [tags, people],
  )
  const value: Recipient[] = useMemo(
    () => [
      ...tags.filter((tg) => draft.inviteTagIds.includes(tg.id)).map((tag) => ({ kind: 'tag' as const, tag })),
      ...(people ?? []).filter((p) => draft.inviteRespondentIds.includes(p.id)).map((person) => ({ kind: 'person' as const, person })),
    ],
    [tags, people, draft.inviteTagIds, draft.inviteRespondentIds],
  )

  // Deduplicated recipients: picked people ∪ everyone in a picked group.
  const recipientCount = useMemo(() => {
    const ids = new Set(draft.inviteRespondentIds)
    const tagIds = new Set(draft.inviteTagIds)
    for (const r of people ?? []) if (r.tags.some((tg) => tagIds.has(tg.id))) ids.add(r.id)
    return ids.size
  }, [people, draft.inviteRespondentIds, draft.inviteTagIds])

  const label = (r: Recipient) => (r.kind === 'tag' ? `${r.tag.name} · ${r.tag.respondent_count}` : r.person.name ? `${r.person.name} <${r.person.email}>` : r.person.email)

  if (people !== null && people.length === 0) {
    return (
      <Field label={t('wizard.desk.distribution.invitesLabel')} optional={t('wizard.desk.optional')}>
        <Typography sx={{ fontSize: 13, color: c.ink56 }}>
          {t('wizard.distribution.emailEmptyBody')}{' '}
          <Link href="#/respondents" target="_blank" rel="noopener" sx={{ color: c.coral, fontWeight: 700 }}>
            {t('wizard.distribution.emailEmptyCta')}
          </Link>
        </Typography>
      </Field>
    )
  }

  return (
    <Field
      label={t('wizard.desk.distribution.invitesLabel')}
      optional={t('wizard.desk.optional')}
      help={recipientCount > 0 ? t('wizard.desk.distribution.invitesCount', { count: recipientCount }) : t('wizard.desk.distribution.invitesHelp')}
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        loading={people === null}
        options={options}
        value={value}
        groupBy={(o) => (o.kind === 'tag' ? t('wizard.distribution.emailGroups') : t('wizard.distribution.emailPeople'))}
        getOptionLabel={label}
        isOptionEqualToValue={(a, b) => recipientKey(a) === recipientKey(b)}
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase()
          if (!q) return opts
          return opts.filter((o) =>
            o.kind === 'tag' ? o.tag.name.toLowerCase().includes(q) : o.person.email.toLowerCase().includes(q) || o.person.name.toLowerCase().includes(q),
          )
        }}
        onChange={(_, next) =>
          onChange({
            inviteTagIds: next.flatMap((r) => (r.kind === 'tag' ? [r.tag.id] : [])),
            inviteRespondentIds: next.flatMap((r) => (r.kind === 'person' ? [r.person.id] : [])),
          })
        }
        renderOption={({ key, ...props }, o, { selected }) => (
          <Box component="li" key={key} {...props} sx={{ display: 'flex', gap: 1, fontSize: 13.5 }}>
            <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
            {o.kind === 'tag' ? <GroupOutlinedIcon sx={{ fontSize: 16, color: c.ink48 }} /> : <PersonOutlineIcon sx={{ fontSize: 16, color: c.ink48 }} />}
            <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(o)}</Box>
          </Box>
        )}
        renderValue={(selected, getItemProps) =>
          selected.map((o, index) => {
            const { key, ...itemProps } = getItemProps({ index })
            return <Chip key={key} size="small" label={label(o)} {...itemProps} sx={{ borderRadius: 99, fontWeight: 600, fontSize: 12.5, bgcolor: '#F0F0F4' }} />
          })
        }
        renderInput={(params) => (
          <DeskInput
            {...params}
            placeholder={value.length ? '' : t('wizard.desk.distribution.invitesPlaceholder')}
            slotProps={{ input: params.InputProps, htmlInput: params.inputProps }}
            sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important', minHeight: 42, py: '4px !important', pl: '8px !important' } }}
          />
        )}
      />
    </Field>
  )
}
